import type { Middleware } from 'openapi-fetch';
import { ulid } from 'ulid';
import { tokenStore } from '../auth/index';
import {
  idempotencyKeyFor,
  lookupOrCreatePersistentKey,
  releaseIdempotencyKey,
  releasePersistentKey,
  requiresIdempotencyKey,
  requiresPersistentSession,
} from '../idempotency/index';

export interface HttpCoreOptions {
  /** "WEB" for storefront, "ADMIN" for backoffice. Per fe-be-contract §2.1. */
  channel: 'WEB' | 'ADMIN' | 'APP';
  /** Called when middleware decides a refresh is needed. Resolves to new access token or null. */
  refreshAccessToken: () => Promise<string | null>;
  /** Called when refresh fails terminally; caller should navigate to login. */
  onAuthLost?: () => void;
  /** Optional Sentry breadcrumb hook. */
  reportError?: (err: unknown, ctx: { url: string; status: number }) => void;
}

const SESSION_AUTH_PATH_REGEX =
  /\/(?:admin\/)?v\d+\/(?:auth\/|me(?:$|\/))/;

/**
 * Session-establishing endpoints whose 401 after a successful refresh truly
 * means the session is gone (auth/login, auth/refresh, auth/logout, me, etc.).
 *
 * Any other endpoint returning 401 after a successful refresh is an RBAC
 * denial (semantically 403). It must NOT log the user out — see DEC-UF-BO-FIX
 * after Phase 3 W3 BO 진단 사이클.
 */
const isSessionAuthPath = (url: string): boolean => {
  try {
    return SESSION_AUTH_PATH_REGEX.test(new URL(url).pathname);
  } catch {
    return SESSION_AUTH_PATH_REGEX.test(url);
  }
};

/**
 * Build the openapi-fetch middleware chain per RFC-001 §4.3.
 *
 * Order matters:
 *   1. auth attach
 *   2. idempotency attach
 *   3. trace + channel attach
 *   4. (envelope NOT stripped — typed wrapper does that)
 *   5. 401 single-flight refresh + retry
 */
export const createMiddleware = (opts: HttpCoreOptions): Middleware => {
  const { channel, refreshAccessToken, onAuthLost, reportError } = opts;

  // Idempotency cache keys are derived from the request body. onResponse must
  // release the same key onRequest created, but the request body stream is
  // already consumed by the time the response arrives — a second
  // `request.clone().text()` throws `Request body is already used`, which
  // rejects the whole response and surfaces a false-failure even on a 2xx.
  // Capture the body text once at request time (clone is still readable then)
  // keyed by the Request instance openapi-fetch passes to both hooks.
  const requestBodyText = new WeakMap<Request, string>();

  return {
    async onRequest({ request }) {
      // 1. auth
      const token = tokenStore.token;
      if (token) request.headers.set('Authorization', `Bearer ${token}`);

      // 2. idempotency
      const method = request.method.toUpperCase();
      const url = new URL(request.url);
      const path = url.pathname;
      if (requiresIdempotencyKey(method, path)) {
        // body has already been serialized into request.body by openapi-fetch
        const bodyText = request.body ? await request.clone().text() : '';
        requestBodyText.set(request, bodyText);
        // Flows that traverse external redirects (TossPayments → /checkout/success)
        // need sessionStorage-backed persistence; everything else uses the fast
        // in-memory Map. See writeAllowlist.requiresPersistentSession.
        const key = requiresPersistentSession(method, path)
          ? lookupOrCreatePersistentKey(`${method} ${path} ${bodyText}`)
          : idempotencyKeyFor(method, path, bodyText);
        request.headers.set('Idempotency-Key', key);
      }

      // 3. trace + channel + client version
      if (!request.headers.has('X-Trace-Id')) {
        request.headers.set('X-Trace-Id', ulid());
      }
      request.headers.set('X-Channel', channel);

      return request;
    },

    async onResponse({ request, response }) {
      // 5. 401 refresh
      if (response.status === 401) {
        // C-1: an anonymous visitor (no session ever established in this tab)
        // has no refresh cookie. Calling refresh would only produce a spurious
        // 401 and a redirect to /login that's already there. Skip silently —
        // the 401 stays a 401 for the caller, no refresh noise, no loop.
        if (!tokenStore.hadSession) {
          return response;
        }

        const newToken = await tokenStore.withRefreshLock(refreshAccessToken);
        if (newToken) {
          // retry once with refreshed token
          const retryHeaders = new Headers(request.headers);
          retryHeaders.set('Authorization', `Bearer ${newToken}`);
          const retryReq = new Request(request, { headers: retryHeaders });
          const retried = await fetch(retryReq);
          if (retried.status === 401 && isSessionAuthPath(retryReq.url)) {
            tokenStore.clear();
            if (tokenStore.markAuthLostOnce()) onAuthLost?.();
          }
          return retried;
        }
        tokenStore.clear();
        // C-3: concurrent 401s land here together; only the first redirects.
        if (tokenStore.markAuthLostOnce()) onAuthLost?.();
      }

      // success: release idempotency key (no longer needed). Reuse the body text
      // captured at request time — re-reading the consumed request body here
      // throws and would reject an otherwise-successful response.
      const method = request.method.toUpperCase();
      const url = new URL(request.url);
      if (response.ok && requiresIdempotencyKey(method, url.pathname)) {
        const bodyText = requestBodyText.get(request) ?? '';
        releaseIdempotencyKey(method, url.pathname, bodyText);
        if (requiresPersistentSession(method, url.pathname)) {
          releasePersistentKey(`${method} ${url.pathname} ${bodyText}`);
        }
      }

      return response;
    },

    async onError({ error, request }) {
      reportError?.(error, { url: request.url, status: 0 });
      if (error instanceof Error) return error;
      return new Error(typeof error === 'string' ? error : 'Unknown error');
    },
  };
};
